/**
 * La mitad HTTP. Nota: esta es la mitad *autoritativa*.
 *
 * El socket es un acelerador: le dice a la UI que algo cambió antes de lo que
 * un poll diría. Todo número que el usuario ve puede volver a derivarse de
 * estos endpoints, que es lo que hace que una trama perdida sea un no-evento en
 * vez de una inconsistencia silenciosa. Diseña la feature para que cerrar el
 * socket por completo la degrade a «se actualiza al navegar», nunca a «mal».
 */
import { apiClient } from '@/api/client';
import type { Paginated, ListParams } from '@/api/types';

import type { NotificationDto } from './types';

const BASE = '/api/v1/notifications/';

export const notificationsApi = {
  async list(params?: ListParams & { unread?: 1 }): Promise<Paginated<NotificationDto>> {
    const { data } = await apiClient.get<Paginated<NotificationDto>>(BASE, { params });
    return data;
  },

  async unreadCount(): Promise<number> {
    const { data } = await apiClient.get<{ unread: number }>(`${BASE}unread-count/`);
    return data.unread;
  },

  /** Omit `ids` para marcar leído todo el buzón del que llama. */
  async markRead(ids?: string[]): Promise<number> {
    const { data } = await apiClient.post<{ unread: number }>(
      `${BASE}mark-read/`,
      ids ? { ids } : {},
    );
    return data.unread;
  },

  /** Registra que estas se mostraron. No las marca leídas — ver el panel. */
  async markDelivered(ids: string[]): Promise<number> {
    const { data } = await apiClient.post<{ unread: number }>(`${BASE}mark-delivered/`, { ids });
    return data.unread;
  },

  /**
   * Acuña un ticket de un solo uso para el handshake del WebSocket.
   *
   * Esto pasa por `apiClient`, lo que significa que lleva el access token en
   * memoria y refresca transparentemente cuando está vencido. Es toda la razón
   * del flujo de tickets: la capa del socket nunca ve un JWT, nunca tiene que
   * refrescar uno, y nunca pone uno en una URL.
   */
  async wsTicket(): Promise<{ ticket: string; expires_in: number }> {
    const { data } = await apiClient.post<{ ticket: string; expires_in: number }>(
      `${BASE}ws-ticket/`,
    );
    return data;
  },
};
